/**
 * Data access for the order entity.
 *
 * Reads: ``GET /api/orders/`` (My orders) and ``GET /api/orders/<id>/`` (detail).
 * Writes (create order, chat) live in the product-order/order-chat features so the
 * idempotency key and side effects stay close to their call sites. Object access is
 * enforced server-side (a foreign order yields 404).
 */
import { apiClient, useApiQuery, type QueryState } from "@shared/api";

import type { OrderChatMessage, OrderDetail, OrderListItem } from "./model";

export const ORDERS_PATH = "/orders/";

/** Path of a single order's detail endpoint. */
export function orderPath(orderId: number): string {
  return `/orders/${orderId}/`;
}

/** Path of an order's chat-messages endpoint (GET history / POST send). */
export function orderMessagesPath(orderId: number): string {
  return `/orders/${orderId}/messages/`;
}

/** Path of an order's customer self-cancel endpoint (ТЗ §11). */
export function orderCancelPath(orderId: number): string {
  return `/orders/${orderId}/cancel/`;
}

/** Path of an order's FSM transition endpoint (role + state guarded server-side). */
export function orderTransitionPath(orderId: number): string {
  return `/orders/${orderId}/transition/`;
}

/** Path of the customer purchase-receipt request endpoint (ТЗ №2доп §19-20). */
export function orderRequestReceiptPath(orderId: number): string {
  return `/orders/${orderId}/request-receipt/`;
}

/** WebSocket path of an order's live chat (Channels group ``order_<id>``). */
export function orderChatWsPath(orderId: number): string {
  return `/ws/orders/${orderId}/chat/`;
}

/** Fetch the current user's orders (object-scoped server-side). */
export function useMyOrders(): QueryState<readonly OrderListItem[]> {
  return useApiQuery<readonly OrderListItem[]>(ORDERS_PATH);
}

/** Fetch a single order's detail (participant-only; 404 otherwise). */
export function useOrder(
  orderId: number | null,
): QueryState<OrderDetail> {
  return useApiQuery<OrderDetail>(orderId === null ? "" : orderPath(orderId), {
    enabled: orderId !== null,
    deps: [orderId],
  });
}

/** Fetch an order's chat history (participant-only). */
export function useOrderMessages(
  orderId: number | null,
): QueryState<readonly OrderChatMessage[]> {
  return useApiQuery<readonly OrderChatMessage[]>(
    orderId === null ? "" : orderMessagesPath(orderId),
    { enabled: orderId !== null, deps: [orderId] },
  );
}

/**
 * Self-cancel a fresh order within its window (``POST /api/orders/<id>/cancel/``, ТЗ §11).
 *
 * The server re-checks the window/state, refunds any spent currency (ТЗ §12) and returns the
 * updated order; the client refetches to reflect the new status. A 4xx (window elapsed /
 * not cancellable) surfaces as an {@link ApiError} for the caller to message.
 */
export function cancelOrder(orderId: number): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(orderCancelPath(orderId), {});
}

/**
 * Drive an FSM transition on an order (``POST /api/orders/<id>/transition/``, ТЗ §2.3).
 *
 * The customer's own transitions (e.g. ``declare_payment`` — «Перевод выполнен») go through
 * the same endpoint as the seller panel; authority and source-state validity are re-checked
 * server-side. Returns the updated order so the screen can refetch / reflect the new status.
 */
export function transitionOrder(
  orderId: number,
  transition: string,
): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(orderTransitionPath(orderId), {
    body: { transition },
  });
}

/**
 * Request a purchase receipt for a completed order (``POST /api/orders/<id>/request-receipt/``).
 *
 * Idempotent server-side (by ``receipt_request_state``): a completed order returns to the active
 * feed, its chat reopens and the seller is notified (ТЗ №2доп §19-20). Returns the updated order
 * so the screen reflects the new state.
 */
export function requestOrderReceipt(orderId: number): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(orderRequestReceiptPath(orderId), {});
}

/** Post a chat message via REST (WS-fallback / first send). Returns the persisted row. */
export function postOrderMessage(
  orderId: number,
  text: string,
): Promise<OrderChatMessage> {
  return apiClient.post<OrderChatMessage>(orderMessagesPath(orderId), {
    body: { text, source: "miniapp" },
  });
}

/**
 * Post a chat message with an attachment (image/PDF) via multipart REST.
 *
 * Sends ``FormData`` (``text`` optional, ``attachment`` the file) so the browser sets the
 * multipart boundary itself; ``apiClient`` keeps the Telegram auth header. Returns the
 * persisted row (its ``attachment_url`` is the server-built link).
 */
export function postOrderMessageWithAttachment(
  orderId: number,
  file: File,
  text = "",
): Promise<OrderChatMessage> {
  const formData = new FormData();
  formData.append("source", "miniapp");
  if (text.trim().length > 0) {
    formData.append("text", text.trim());
  }
  formData.append("attachment", file);
  return apiClient.post<OrderChatMessage>(orderMessagesPath(orderId), {
    formData,
  });
}
